/**
 * ChatBot Component Type Definitions
 *
 * Centralized type definitions for type safety and maintainability
 */

// ---------- Enums ----------

export enum MessageType {
  BOT = 'bot',
  USER = 'user',
  SYSTEM = 'system'
}

export enum MessageStatus {
  THINKING = 'thinking',
  PROCESSING = 'processing',
  IDLE = 'idle'
}

// ---------- Core Interfaces ----------

export interface ChatMessage {
  type: MessageType
  message: string
  messageId?: string // Unique identifier from server for deduplication
  documents?: Array<{
    fileName: string
    documentId: string
  }>
  prompts?: string[]
  status?: MessageStatus
  timestamp?: string // ISO timestamp for message ordering
}

export interface RouteParams {
  readonly caseId?: string
}

export interface SessionConfig {
  readonly storagePrefix: string
  readonly globalSessionKey: string
}

// ---------- Component Props ----------

export interface ChatBotProps {
  readonly className?: string
  readonly onError?: (error: Error) => void
  readonly maxRetries?: number
  readonly disabled?: boolean
  readonly placeholder?: string
  readonly autoFocus?: boolean
}

export const defaultChatBotProps: Partial<ChatBotProps> = {
  maxRetries: 3,
  disabled: false,
  autoFocus: false,
  placeholder: 'Type your message...'
}

// ---------- Hook Return Types ----------

export interface UseChatHistoryResult {
  readonly data: readonly ChatMessage[]
  readonly loading: boolean
  readonly error: Error | null
  readonly hasHistory: boolean
  readonly refetch: () => void
}

// ---------- Utility Types ----------

export type CaseId = number
export type MessageIndex = number | null

// ---------- Error Types ----------

export class ChatBotError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ChatBotError'
  }
}

export class GraphQLSubscriptionError extends ChatBotError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, 'GRAPHQL_SUBSCRIPTION_ERROR', context)
  }
}
